export interface ChangelogEntry {
  version: string;
  date: string;
  description?: {
    en: string;
    nl: string;
  };
  changes: {
    type: "feature" | "bugfix" | "improvement" | "breaking";
    description: {
      en: string;
      nl: string;
    };
  }[];
}

export const changelog: ChangelogEntry[] = [
  {
    version: "2026.11.3",
    date: "04-11-2026",
    description: {
      en: "This release makes the Compass hours indication count multidisciplinary quotations by the hours of the selected discipline.",
      nl: "Deze release laat multidisciplinaire aanbiedingen in de Compass-uren-indicatie meetellen met de uren van de gekozen discipline.",
    },
    changes: [
      {
        type: "improvement",
        description: {
          en: "Compass: when you search within one discipline, a multidisciplinary quotation now counts in the hours indication with only the hours of that discipline. The result card shows those hours, or explains why a project does not count.",
          nl: "Compass: zoek je binnen één discipline, dan telt een multidisciplinaire aanbieding nu mee in de uren-indicatie met alleen de uren van die discipline. De resultaatkaart toont die uren, of legt uit waarom een project niet meetelt.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Compass is now called \"comparable projects\" instead of \"estimating by analogy\".",
          nl: "Compass heet nu \"vergelijkbare projecten\" in plaats van \"analoog ramen\".",
        },
      },
    ],
  },
  {
    version: "2026.11.2",
    date: "03-11-2026",
    description: {
      en: "This release adds Compass for members of the Compass group: find comparable earlier projects and the hours quoted for them.",
      nl: "Deze release voegt Compass toe voor leden van de Compass-groep: vind vergelijkbare eerdere projecten en de uren die daarvoor zijn geoffreerd.",
    },
    changes: [
      {
        type: "feature",
        description: {
          en: "New Compass page (under Projects, for members of the Compass group): describe a new request in a simple form and see comparable projects with project number, man-hours per role, an hours indication (min/median/max) and an AI comparison. Only works at the office or via VPN.",
          nl: "Nieuwe pagina Compass (onder Projecten, voor leden van de Compass-groep): beschrijf een nieuwe aanvraag in een eenvoudig formulier en zie vergelijkbare projecten met projectnummer, manuren per functie, een uren-indicatie (min/mediaan/max) en een AI-vergelijking. Werkt alleen op kantoor of via VPN.",
        },
      },
      {
        type: "feature",
        description: {
          en: "Compass: rate each result as Good, Fair or Wrong and, for Fair and Wrong, give a reason and an optional explanation. This helps improve the search.",
          nl: "Compass: beoordeel elk resultaat als Goed, Matig of Fout en geef bij Matig en Fout een reden en eventueel een toelichting. Dit helpt om het zoeken te verbeteren.",
        },
      },
    ],
  },
  {
    version: "2026.11.1",
    date: "03-11-2026",
    description: {
      en: "This release lets you choose which columns you see in the Timesheet, and adds your work location.",
      nl: "Deze release laat je zelf kiezen welke kolommen je in de Timesheet ziet, en voegt je werklocatie toe.",
    },
    changes: [
      {
        type: "feature",
        description: {
          en: "Timesheet: new Columns tab on the right side of the table. Tick a column to show it, such as Description, Customer or Hour code.",
          nl: "Timesheet: nieuw tabblad Kolommen rechts naast de tabel. Vink een kolom aan om hem te tonen, zoals Description, Customer of Hour code.",
        },
      },
      {
        type: "feature",
        description: {
          en: "Timesheet: new Work location column (office, at the customer or at home), available via the Columns tab.",
          nl: "Timesheet: nieuwe kolom Work location (kantoor, bij de klant of thuis), te kiezen via het tabblad Kolommen.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Timesheet: the Year and Week columns are narrower, so more of the table fits on screen.",
          nl: "Timesheet: de kolommen Jaar en Week zijn smaller, zodat er meer van de tabel op je scherm past.",
        },
      },
      {
        type: "bugfix",
        description: {
          en: "Timesheet: the day columns and the Filters/Columns tabs now follow the language you choose.",
          nl: "Timesheet: de dagkolommen en de tabbladen Filters/Kolommen volgen nu de gekozen taal.",
        },
      },
      {
        type: "bugfix",
        description: {
          en: "Timesheet: the 'Expand all' button resets when the table is rebuilt after changing columns.",
          nl: "Timesheet: de knop 'Alles uitklappen' zet zich terug als de tabel na het wijzigen van kolommen opnieuw wordt opgebouwd.",
        },
      },
    ],
  },
  {
    version: "2026.10.2",
    date: "14-10-2026",
    description: {
      en: "This release adds a feedback page, so you can tell us what you want to see in the app.",
      nl: "Deze release voegt een feedbackpagina toe, zodat je kunt laten weten wat je in de app wilt zien.",
    },
    changes: [
      {
        type: "feature",
        description: {
          en: "New Feedback page (under Help & support): vote for up to 3 features you want first, and send an idea, bug or compliment.",
          nl: "Nieuwe pagina Feedback (onder Help & Support): stem op maximaal 3 features die je als eerste wilt, en stuur een idee, bug of compliment.",
        },
      },
      {
        type: "feature",
        description: {
          en: "When you open the app, it occasionally asks for your feedback (at most once every 30 days). Choose 'Don't ask again' to turn this off.",
          nl: "Bij het openen vraagt de app af en toe om je feedback (hooguit eens per 30 dagen). Kies 'Niet meer vragen' om dit uit te zetten.",
        },
      },
    ],
  },
  {
    version: "2026.10.1",
    date: "09-10-2026",
    description: {
      en: "This release makes My projects and Trend & forecast easier to read, translates the dashboard and makes the timesheet follow-up more accurate.",
      nl: "Deze release maakt Mijn projecten en Trend & prognose makkelijker te lezen, vertaalt het dashboard en maakt de opvolging van urenweekstaten nauwkeuriger.",
    },
    changes: [
      {
        type: "improvement",
        description: {
          en: "The projects card on My projects shows a clear count instead of a donut: the total number of projects, a ratio bar and the split between billable and internal.",
          nl: "De projectenkaart op Mijn projecten toont een duidelijke teller in plaats van een donut: het totaal aantal projecten, een verhoudingsbalk en de verdeling tussen factureerbaar en intern.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "In Trend & forecast the budget line now appears in the legend, and its label no longer overlaps the bars.",
          nl: "In Trend & prognose staat de budgetlijn nu in de legenda, en het label valt niet meer over de staven.",
        },
      },
      {
        type: "bugfix",
        description: {
          en: "The percentage of complete timesheets was too low because former employees were counted. It is now calculated over active employees only, and booking more than your contract hours counts as complete.",
          nl: "Het percentage complete urenweekstaten viel te laag uit omdat oud-medewerkers meetelden. Het wordt nu alleen over actieve medewerkers berekend, en meer boeken dan je contracturen telt als compleet.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Incomplete timesheets shows how old the data is ('Data from HH:MM'), so you can see whether it may lag behind the timesheet.",
          nl: "Incomplete urenweekstaten toont hoe oud de gegevens zijn ('Gegevens van HH:MM'), zodat je ziet of ze mogelijk achterlopen op de weekstaat.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "The dashboard is now available in Dutch and English, including chart labels and tooltips.",
          nl: "Het dashboard is nu beschikbaar in het Nederlands en Engels, inclusief grafieklabels en tooltips.",
        },
      },
    ],
  },
  {
    version: "2026.9.6",
    date: "29-09-2026",
    description: {
      en: "This release makes the booked hours on the project pages reliable: Hours budget, Trend & forecast and My projects now all show the same hours. It also brings a smarter people search.",
      nl: "Deze release maakt de geboekte uren op de projectpagina's betrouwbaar: Urenbudget, Trend & prognose en Mijn projecten tonen nu allemaal dezelfde uren. Daarnaast is het zoeken naar collega's slimmer geworden.",
    },
    changes: [
      {
        type: "bugfix",
        description: {
          en: "Trend & forecast missed hours on large projects (more than 5,000 hour entries): the chart stopped too early and could wrongly show a project as stalled. All hours are now included.",
          nl: "Trend & prognose miste uren bij grote projecten (meer dan 5.000 urenregels): de grafiek stopte te vroeg en kon een project ten onrechte als stilliggend tonen. Nu worden alle uren meegenomen.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Hours budget, Trend & forecast and the hours budget column in My projects now use the same booked hours: every hour entry on the project, including drafts and hours not yet approved. Only cancelled and returned hours are left out. Previously Hours budget and My projects showed the hours as processed by finance, which lag behind and include financial corrections. The figures can therefore differ slightly from the financial administration.",
          nl: "Urenbudget, Trend & prognose en de kolom urenbudget in Mijn projecten gebruiken nu dezelfde geboekte uren: alle urenregels op het project, ook concepten en uren die nog niet zijn goedgekeurd. Alleen geannuleerde en teruggestuurde uren tellen niet mee. Eerder toonden Urenbudget en Mijn projecten de uren zoals financiën die verwerkt, die achterlopen en financiële correcties bevatten. De cijfers kunnen daardoor licht afwijken van de financiële administratie.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Smarter people search: every word you type must match (in any order, so 'jan berg' finds 'Jan van den Berg'), accents are ignored ('jose' finds 'José') and the best matches come first. Search now only looks at names, email name, job title, department, office, phone numbers and skills, so results no longer look random.",
          nl: "Slimmer zoeken naar collega's: elk woord dat je typt moet voorkomen (in willekeurige volgorde, dus 'jan berg' vindt 'Jan van den Berg'), accenten worden genegeerd ('jose' vindt 'José') en de beste treffers staan bovenaan. Er wordt alleen nog gezocht in naam, mailnaam, functie, afdeling, kantoor, telefoonnummers en skills, zodat de resultaten niet meer willekeurig lijken.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "The help center has a calmer, consistent look: all cards and panels share the same flat style.",
          nl: "Het helpcentrum heeft een rustigere, consistente uitstraling: alle kaarten en panelen hebben dezelfde vlakke stijl.",
        },
      },
    ],
  },
  {
    version: "2026.9.5",
    date: "28-09-2026",
    description: {
      en: "This release makes 'Trend & forecast' smarter (a recent pace and a breakdown by discipline), adds help buttons to My projects and the project detail page, and brings a completely redesigned help center.",
      nl: "Deze release maakt 'Trend & prognose' slimmer (een recent tempo en een uitsplitsing per discipline), voegt helpknoppen toe aan Mijn projecten en de projectdetailpagina, en brengt een volledig vernieuwd helpcentrum.",
    },
    changes: [
      {
        type: "improvement",
        description: {
          en: "The forecast now uses the recent pace: a weighted average of the last 8 completed weeks, with recent weeks counting more. It follows the phase a project is in (start-up, peak or wind-down) instead of averaging over the whole period. Quiet weeks up to today now count, a project with no hours for 8 weeks is shown as stalled, and the average over the whole period remains visible for comparison.",
          nl: "De prognose gebruikt nu het recente tempo: een gewogen gemiddelde van de laatste 8 afgeronde weken, waarbij recente weken zwaarder tellen. Het tempo volgt zo de fase waarin een project zit (opstart, piek of afronding) in plaats van een gemiddelde over de hele looptijd. Stille weken tot vandaag tellen nu mee, een project zonder uren in de laatste 8 weken wordt als stilliggend getoond, en het gemiddelde over de looptijd blijft ter vergelijking zichtbaar.",
        },
      },
      {
        type: "feature",
        description: {
          en: "New 'By discipline' view on the trend chart: stacked bars per discipline, a cumulative line per discipline as a % of its own budget with its own forecast, and a table with budget, booked hours, recent pace and expected end per discipline.",
          nl: "Nieuwe weergave 'Per discipline' in de trendgrafiek: gestapelde staven per discipline, per discipline een cumulatieve lijn als % van het eigen budget met een eigen prognose, en een tabel met budget, geboekte uren, recent tempo en verwacht einde per discipline.",
        },
      },
      {
        type: "feature",
        description: {
          en: "New help button (?) at the top right of the project detail page. It explains what 'Hours budget' and 'Trend & forecast' show and how to use them, including how the chart legend works: click to show or hide an item, double-click to show only that item, and double-click it again to show everything.",
          nl: "Nieuwe helpknop (?) rechtsboven op de projectdetailpagina. Die legt uit wat 'Urenbudget' en 'Trend & prognose' laten zien en hoe je ze gebruikt, inclusief hoe de legenda van de grafiek werkt: klik om een onderdeel aan of uit te zetten, dubbelklik om alleen dat onderdeel te tonen, en dubbelklik nog een keer om alles weer te tonen.",
        },
      },
      {
        type: "feature",
        description: {
          en: "My projects now also has a help button (?) at the top right. It explains the period filter, the three cards (including exactly how productivity is calculated), the legend, the client table and the quick actions.",
          nl: "Mijn projecten heeft nu ook een helpknop (?) rechtsboven. Die legt de periodefilter uit, de drie kaarten (inclusief hoe productiviteit precies wordt berekend), de legenda, de klantentabel en de snelle acties.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "The help center has been redesigned: full-text search with highlighted matches, topics per category, shareable links to individual articles, a 'Go to this page' button and a 'Was this helpful?' check. You only see articles that apply to your role, and the existing articles have been updated and corrected.",
          nl: "Het helpcentrum is vernieuwd: zoeken in de volledige tekst met gemarkeerde treffers, onderwerpen per categorie, deelbare links naar losse artikelen, een knop 'Naar deze pagina' en de vraag 'Was dit nuttig?'. Je ziet alleen artikelen die voor jouw rol gelden, en de bestaande artikelen zijn bijgewerkt en gecorrigeerd.",
        },
      },
      {
        type: "bugfix",
        description: {
          en: "Fixed a week-number calculation in the backend that, on a server running in a timezone with daylight saving time, placed hours one week too early during summer time in 2026. Production (UTC) was not affected.",
          nl: "Weeknummerberekening in de backend gerepareerd: op een server in een tijdzone met zomertijd kwamen uren in de zomer van 2026 een week te vroeg uit. Productie (UTC) had hier geen last van.",
        },
      },
    ],
  },
  {
    version: "2026.9.4",
    date: "22-09-2026",
    description: {
      en: "This release adds a new 'Project progress' timeline to the project detail page, showing booked hours over time against the budget, with a forecast of when the budget will run out.",
      nl: "Deze release voegt een nieuwe 'Project voortgang'-tijdlijn toe aan de projectdetailpagina, met geboekte uren over tijd afgezet tegen het budget en een verwachting van wanneer het budget op is.",
    },
    changes: [
      {
        type: "feature",
        description: {
          en: "New 'Project progress' tab on the project detail page: a chart showing hours booked per week, a cumulative total, and a budget line, running from the first to the last booked hour entry on the project.",
          nl: "Nieuwe tab 'Project voortgang' op de projectdetailpagina: een grafiek met per week geboekte uren, een cumulatief totaal en een budgetlijn, lopend van de eerste tot de laatste geboekte urenregel op het project.",
        },
      },
      {
        type: "feature",
        description: {
          en: "The progress chart now includes a forecast: based on the average booking pace, it projects when the remaining budget will run out (or shows the week in which the budget was already reached), together with KPI tiles for remaining budget, average pace, expected end date and the active period.",
          nl: "De voortgangsgrafiek bevat nu een verwachting: op basis van het gemiddelde boekingstempo wordt geschat wanneer het resterende budget op is (of wordt de week getoond waarin het budget al werd bereikt), samen met tegels voor resterend budget, gemiddeld tempo, verwachte einddatum en de actieve periode.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "The existing progress overview (budget vs. booked hours per department and task) has been renamed to 'Project hours status' and now also shows a total budget bar for the whole project, clearly separated from the breakdown per department and task.",
          nl: "Het bestaande voortgangsoverzicht (budget vs. geboekte uren per afdeling en taak) is hernoemd naar 'Project uren status' en toont nu ook een totaalbalk voor het hele project, duidelijk gescheiden van de uitsplitsing per afdeling en taak.",
        },
      },
    ],
  },
  {
    version: "2026.9.3",
    date: "21-09-2026",
    description: {
      en: "This release introduces a redesigned Project progress view and fixes several smaller issues on the Projects and home pages.",
      nl: "Deze release introduceert een vernieuwd Project voortgang-overzicht en verhelpt een aantal kleinere problemen op de Projecten- en startpagina.",
    },
    changes: [
      {
        type: "feature",
        description: {
          en: "The 'Hours by department' chart has been replaced by a new 'Project progress' view: an expandable list showing budgeted vs. booked hours per department and per task, with an 'expand all' option.",
          nl: "De 'Uren per afdeling'-grafiek is vervangen door een nieuw 'Project voortgang'-overzicht: een uitklapbare lijst met begrote en geboekte uren per afdeling en per taak, met een knop om alles in één keer uit te klappen.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "The Projects page now shows loading indicators on the charts and a clear status message in the projects table while data is loading or when something goes wrong.",
          nl: "De Projecten-pagina toont nu laadindicatoren op de grafieken en een duidelijke statusmelding in de projectentabel terwijl gegevens laden of als er iets misgaat.",
        },
      },
      {
        type: "bugfix",
        description: {
          en: "Fixed a bug where expanding a client row in the projects table could occasionally fail with a console error.",
          nl: "Een fout opgelost waardoor het uitklappen van een klantregel in de projectentabel af en toe kon mislukken met een consolefout.",
        },
      },
      {
        type: "bugfix",
        description: {
          en: "Fixed the hours donut chart on the Projects page sometimes appearing narrow and off-center when there were many customers with long names.",
          nl: "De uren-donutgrafiek op de Projecten-pagina werd soms smal en niet gecentreerd weergegeven bij veel klanten met lange namen; dit is verholpen.",
        },
      },
      {
        type: "bugfix",
        description: {
          en: "Fixed a bug where the 'New colleague' ribbon and welcome dialog on the home page could silently fail to appear if another background request failed at the same time.",
          nl: "Een fout opgelost waardoor de 'Nieuwe collega'-ribbon en het welkomstvenster op de startpagina soms niet verschenen als een andere achtergrondaanvraag tegelijk mislukte.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Role and permission checks (admin, tester, project intake access) are now faster and more reliable, using a single request instead of six on every page navigation.",
          nl: "Rol- en permissiecontroles (admin, tester, project intake toegang) zijn nu sneller en betrouwbaarder, met één aanvraag in plaats van zes bij elke paginanavigatie.",
        },
      },
    ],
  },
  {
    version: "2026.9.2",
    date: "18-09-2026",
    description: {
      en: "This release fixes a bug on the Projects page and makes the client rows in the projects table easier to expand.",
      nl: "Deze release verhelpt een fout op de Projecten-pagina en maakt de klantregels in het projectenoverzicht makkelijker uit te klappen.",
    },
    changes: [
      {
        type: "bugfix",
        description: {
          en: "Selecting a colleague who is registered as a contract worker on the Projects page no longer shows an error and now loads their project data correctly.",
          nl: "Het selecteren van een collega die als contractwerker geregistreerd staat op de Projecten-pagina geeft niet langer een foutmelding en laadt nu de projectgegevens correct.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Clicking anywhere on a client row in the projects table now expands or collapses it, not just the expand arrow.",
          nl: "Klikken op een willekeurige plek op een klantregel in het projectenoverzicht klapt deze nu open of dicht, niet alleen op het pijltje.",
        },
      },
    ],
  },
  {
    version: "2026.9.1",
    date: "17-09-2026",
    description: {
      en: "This release puts new colleagues in the spotlight on the home page and improves the Projects page.",
      nl: "Deze release zet nieuwe collega's in de spotlights op de startpagina en verbetert de Projecten-pagina.",
    },
    changes: [
      {
        type: "feature",
        description: {
          en: "Employees who started in the current calendar month now get a 'New colleague' ribbon on their card on the home page, showing their start date.",
          nl: "Medewerkers die deze kalendermaand zijn gestart krijgen nu een 'Nieuwe collega'-ribbon op hun kaart op de startpagina, met daarop de startdatum.",
        },
      },
      {
        type: "feature",
        description: {
          en: "A welcome dialog now appears once a month on the home page, showing every colleague who started that calendar month.",
          nl: "Er verschijnt nu eenmaal per maand een welkomstvenster op de startpagina met alle collega's die deze kalendermaand zijn gestart.",
        },
      },
      {
        type: "feature",
        description: {
          en: "Testers can now see the hours-budget progress bar column in the Projects grid on the My Projects page.",
          nl: "Testers kunnen nu de voortgangsbalk in de urenbudget-kolom zien in het projectenoverzicht op de Mijn Projecten-pagina.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "The hours card on the Projects page has been redesigned so you can now switch between a Client view and a Project view.",
          nl: "De urenkaart op de Projecten-pagina is vernieuwd, waardoor je nu kunt wisselen tussen een Klanten- en een Projectenweergave.",
        },
      },
    ],
  },
  {
    version: "2026.7.2",
    date: "14-07-2026",
    changes: [
      {
        type: "improvement",
        description: {
          en: "Project Intake: Customer dropdown now shows Account Number and City from Visiting Address (similar layout to Project Template)",
          nl: "Project Intake: Klant dropdown toont nu Account Nummer en Stad uit Bezoekadres (zelfde layout als Project Template)",
        },
      },
    ],
  },
  {
    version: "2026.7.1",
    date: "09-07-2026",
    changes: [
      {
        type: "feature",
        description: {
          en: "Timesheet: Added Customer/Client column with filtering, sorting, and grouping support",
          nl: "Timesheet: Klant/Client kolom toegevoegd met filter-, sorteer- en groepeerfunctionaliteit",
        },
      },
    ],
  },
  {    version: "2026.5.6",
    date: "16-06-2026",
    changes: [
      {
        type: "bugfix",
        description: {
          en: "Fix Content Security Policy violation - Graph API group membership checks now bypass Service Worker to prevent CSP errors",
          nl: "CSP (Content Security Policy) schending opgelost - Graph API groepcontroles omzeilen nu de Service Worker",
        },
      },
    ],
  },
  {    version: "2026.5.6",
    date: "15-06-2026",
    changes: [
      {
        type: "feature",
        description: {
          en: "Added Email Signature Generator - Create professional Outlook signatures with name, job title, office address, phone, mobile, email, and website",
          nl: "E-mailhandtekening generator toegevoegd - Maak professionele Outlook-handtekeningen met naam, functie, kantooradres, telefoon, mobiel, e-mail en website",
        },
      },
      {
        type: "feature",
        description: {
          en: "Email signature can be copied to clipboard and pasted directly into Outlook",
          nl: "E-mailhandtekening kan naar klembord worden gekopieerd en rechtstreeks in Outlook worden geplakt",
        },
      },
    ],
  },
  {
    version: "2026.5.5",
    date: "05-06-2026",
    changes: [
      {
        type: "improvement",
        description: {
          en: "Project Intake: Search text in Team Members dropdown now clears automatically after selecting a user",
          nl: "Project Intake: Zoektekst in Team Members dropdown wordt nu automatisch geleegd na selectie",
        },
      },
    ],
  },
  {
    version: "2026.5.4",
    date: "05-06-2026",
    changes: [
      {
        type: "feature",
        description: {
          en: "Project Intake: Added Quote Expected Sent Date field (defaults to +7 days)",
          nl: "Project Intake: Veld 'Quote verwacht verzonden datum' toegevoegd (standaard +7 dagen)",
        },
      },
      {
        type: "feature",
        description: {
          en: "Project Intake: Added Project Template dropdown filtered from Dynamics",
          nl: "Project Intake: Project Template dropdown toegevoegd gefilterd uit Dynamics",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Project Intake: Project Manager and Team Members now exclude each other (prevents same user selection)",
          nl: "Project Intake: Project Manager en Team Members sluiten elkaar uit (verhindert dubbele selectie)",
        },
      },
      {
        type: "bugfix",
        description: {
          en: "Fixed permission loading on protected routes - user roles now sync immediately when navigating to Project Intake or other admin pages",
          nl: "Permissies laadden niet goed in op beveiligde routes - gebruikersrollen worden nu direct gesynchroniseerd",
        },
      },
    ],
  },
  {
    version: "2026.5.3",
    date: "27-05-2026",
    changes: [
      {
        type: "improvement",
        description: {
          en: "Dashboard and Incomplete Timesheets menu items are now always visible. Non-authorized users see them greyed out with a lock icon instead of hidden.",
          nl: "Dashboard en Incomplete Timesheets zijn nu altijd zichtbaar in het menu. Gebruikers zonder toegang zien ze grijs met een slotje in plaats van verborgen.",
        },
      },
    ],
  },
  {
    version: "2026.5.2",
    date: "27-05-2026",
    changes: [
      {
        type: "improvement",
        description: {
          en: "Project intake is now restricted to members of the azg-role_D365-PROD-CE-ProjectIntake group. Non-members see the menu item with a lock icon.",
          nl: "Project intake is nu alleen toegankelijk voor leden van de groep azg-role_D365-PROD-CE-ProjectIntake. Niet-leden zien het menu-item met een slotje.",
        },
      },
      {
        type: "bugfix",
        description: {
          en: "Customer dropdown in project intake now only shows active customer accounts (pyl_accounttype = 200000000).",
          nl: "De klantdropdown in de project intake toont nu alleen actieve klantaccounts (pyl_accounttype = 200000000).",
        },
      },
    ],
  },
  {
    version: "2026.5.1",
    date: "26-05-2026",
    description: {
      en: "This release adds a project intake form.",
      nl: "Deze release voegt een project intake formulier toe.",
    },
    changes: [
      {
        type: "feature",
        description: {
          en: "New project intake form for submitting project proposals. Includes project manager, customer, contracting unit, billing method, start date, duration, estimated revenue, progress, probability and team members.",
          nl: "Nieuw project intake formulier voor het aanmaken van projectvoorstellen. Bevat velden voor projectmanager, klant, contracterende eenheid, factureringsmethode, startdatum, duur, geschatte omzet, voortgang, kans en teamleden.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Help & support moved to the bottom of the navigation drawer for better discoverability.",
          nl: "Help & support verplaatst naar de onderkant van de navigatiebalk voor betere vindbaarheid.",
        },
      },
    ],
  },
  {
    version: "2026.4.0",
    date: "15-04-2026",
    description: {
      en: "This release fixes email bilingual message rendering and improves API reliability with exponential backoff retry logic.",
      nl: "Deze release corrigeert de weergave van tweetalige e-mailberichten en verbetert de API-betrouwbaarheid met exponentiële backoff retry-logica.",
    },
    changes: [
      {
        type: "bugfix",
        description: {
          en: "Fixed bilingual email generation - English version now correctly renders in English instead of Dutch.",
          nl: "Tweetalige e-mailgeneratie gecorrigeerd - Engelse versie wordt nu correct in het Engels weergegeven in plaats van Nederlands.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Added exponential backoff retry logic for API calls to handle transient failures (3 attempts with 500ms, 1s, 2s delays).",
          nl: "Exponentiële backoff retry-logica toegevoegd voor API-oproepen om voorbijgaande storingen af te handelen (3 pogingen met 500ms, 1s, 2s vertragingen).",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Enhanced error tracking with Sentry integration - failed API retries are now logged with structured debugging information.",
          nl: "Verbeterde foutopsporing met Sentry-integratie - mislukte API-pogingen worden nu vastgelegd met gestructureerde debuginformatie.",
        },
      },
    ],
  },
  {
    version: "2026.2.1",
    date: "24-02-2026",
    description: {
      en: "This release improves performance with smart caching and fixes week number calculation issues.",
      nl: "Deze release verbetert de prestaties met slimme caching en lost weeknummer berekeningsproblemen op.",
    },
    changes: [
      {
        type: "improvement",
        description: {
          en: "Added intelligent caching for expensive API queries (4-hour TTL for time entries, 24-hour for user details) with manual hard-refresh option.",
          nl: "Intelligente caching toegevoegd voor zware API queries (4 uur TTL voor urenstaten, 24 uur voor gebruikersgegevens) met mogelijkheid tot handmatig verversen.",
        },
      },
      {
        type: "bugfix",
        description: {
          en: "Fixed week number calculation for timesheet - now accurately calculates ISO 8601 week numbers.",
          nl: "Weeknummer berekening in timesheet gecorrigeerd - berekent nu nauwkeurig ISO 8601 weeknummers.",
        },
      },
    ],
  },
  {
    version: "2026.1.4",
    date: "28-01-2026",
    description: {
      en: "This release introduces Progressive Web App (PWA) capabilities with automatic update notifications and intelligent caching for improved performance.",
      nl: "Deze release introduceert Progressive Web App (PWA) functionaliteit met automatische update notificaties en intelligente caching voor verbeterde prestaties.",
    },
    changes: [
      {
        type: "feature",
        description: {
          en: "PWA update notifications - you will now be notified when a new version is available with option to reload.",
          nl: "PWA update notificaties - je krijgt nu een melding wanneer een nieuwe versie beschikbaar is met optie om te herladen.",
        },
      },
      {
        type: "feature",
        description: {
          en: "Added Project Administration help section with detailed instructions for managing timesheets.",
          nl: "Project Administratie help sectie toegevoegd met gedetailleerde instructies voor het beheren van urenstaten.",
        },
      },
    ],
  },
  {
    version: "2026.1.3",
    date: "22-01-2026",
    description: {
      en: "This release adds better user filtering capabilities for administrators with enhanced worker type visibility.",
      nl: "Deze release voegt betere filteropties toe voor beheerders met verbeterde zichtbaarheid van werknemertype.",
    },
    changes: [
      {
        type: "improvement",
        description: {
          en: "Added worker type chip display for contractors in incomplete timesheets overview.",
          nl: "Weergave van werknemertype chip toegevoegd voor contractwerkers in incompleet urenoverzicht.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Enhanced filtering of bookable resources - only active users with active system accounts are shown.",
          nl: "Verbeterde filtering van boekbare resources - alleen actieve gebruikers met actieve systeemaccounts worden getoond.",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Contractors are now visible under 'Overtime' section.",
          nl: "Contractwerkers zijn nu zichtbaar onder 'Meeruren'",
        },
      },
    ],
  },
  {
    version: "2025.12.5",
    date: "31-12-2025",
    description: {
      en: "This release focuses on improving the user experience with new installation capabilities and better data management. I have also added the ability to copy data from the timesheet grid, making it easier to work with your data in external applications.",
      nl: "Deze release richt zich op het verbeteren van de gebruikerservaring met nieuwe installatiemogelijkheden en beter gegevensbeheer. Ik heb ook de mogelijkheid toegevoegd om data te kunnen kopiëren uit het timesheet grid, waardoor je gemakkelijker met je gegevens in externe applicaties kunt werken.",
    },
    changes: [
      {
        type: "feature",
        description: {
          en: "The app can be installed by clicking the download icon in the top right corner. This will improve user accessibility and performance.",
          nl: "De app kan worden geïnstalleerd door op het download-icoon rechtsboven te klikken. Dit verbetert de toegankelijkheid en prestaties voor gebruikers.",
        },
      },
      {
        type: "feature",
        description: {
          en: "Added holiday task validation - checks if the correct task is used on public holidays.",
          nl: "Controle of juiste taak is gebruikt op feestdagen.",
        },
      },
      {
        type: "feature",
        description: {
          en: "Added clipboard support - cells can now be copied with Ctrl+C.",
          nl: "Klembord ondersteuning toegevoegd - cellen kunnen nu worden gekopieerd met Ctrl+C.",
        },
      },
      {
        type: "feature",
        description: {
          en: "Added multi-year timesheet data loading (2025 onwards).",
          nl: "Meerdere jaren timesheet data laden toegevoegd (vanaf 2025).",
        },
      },
      {
        type: "improvement",
        description: {
          en: "Changelog will appear when new version is available.",
          nl: "Changelog verschijnt automatisch wanneer een nieuwe versie beschikbaar is.",
        },
      },
    ],
  },
  {
    version: "2025.12.4",
    date: "30-12-2025",
    description: {
      en: "Happy holidays! This release brings you the ability to export your timesheet data to Excel and introduces the new changelog feature.",
      nl: "Fijne feestdagen! Deze release brengt je de mogelijkheid om je timesheet data te exporteren naar Excel en introduceert de nieuwe changelog functie.",
    },
    changes: [
      {
        type: "feature",
        description: {
          en: "Added Excel export functionality.",
          nl: "Excel export functionaliteit toegevoegd.",
        },
      },
      {
        type: "bugfix",
        description: {
          en: "Fixed week filter not applying correctly in timesheet data.",
          nl: "Week filter werkte niet correct in timesheet data, dit is opgelost.",
        },
      },
      {
        type: "feature",
        description: {
          en: "Added a changelog.",
          nl: "Changelog toegevoegd.",
        },
      },
      {
        type: "feature",
        description: {
          en: "Added a new christmas theme for the app.",
          nl: "Changelog toegevoegd.",
        },
      },
    ],
  },
];
